import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useParams, useNavigate } from 'react-router-dom';

import { fetch_one } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup
} from '@mui/material';

import {
  Button,
  Page,
  Select,
  TextInput,
  Typography
} from '../../../components/v2/styled';
import { LanguagePicker } from '../../../components/v2/LanguagePicker';
import { SnackSaved } from '../../../components/v2/snacks/Saved';

import { QuestionnaireResult } from '../../../models/QuestionnaireResult';

const { DateTime } = require("luxon");

type Props = {};

export const ClientQuestionnaireEdit: FunctionComponent<Props> = ({}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const {t, i18n} = useTranslation(['translations']);
  const params = useParams();

  const [objectId, setObjectId] = useState<string | undefined>(params.id);
  const [savedOpen, setSavedOpen] = useState<boolean>(false);
  const [agrees, setAgrees] = useState<boolean>(false);
  const [mainObject, setMainObject] = useState<QuestionnaireResult>();

  const [errors, setErrors] = useState<{[z:string]: boolean}>({});

  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ['client', "questionnaire_result", objectId],
    queryFn: () =>
      fetch_one<QuestionnaireResult>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
        {
          object: 'questionnaire_result',
          id: objectId,
          fields: [
            'id', 'extra_data', 'practice', 'questionnaire', 'completed_at', 'is_locked'
          ],
          sub_fields: {
            practice: ['id', 'name'],
            questionnaire: ['id', 'name', 'extra_data', 'content_type']
          },
        }
      ),
    enabled: !!objectId,
  });








  const mutationSave = useMutation({
    mutationFn: (pars: {
      formData: any;
      keepOpen?: boolean
    }) => {
      return fetch_one<QuestionnaireResult>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/save`,
        pars.formData
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setSavedOpen(true);
    },
  });









  let calcPoints = (question:any, answer:any) => {
    let points = 0;
    let maxPoints = question.quiz_points;

    if (maxPoints > 0) {
      if (question.type === 'open') {
        if ((answer || '').toLowerCase() === (question.correct_open || '').toLowerCase()) {
          points = maxPoints;
        }
      }
      if (question.type === 'single_radio') {
        let correct_options = ((question.correct_options || {})[i18n.resolvedLanguage || 'nl'] || (question.correct_options || {})['nl']).split(/\r?\n/);
        
        if (correct_options.indexOf(answer) > -1) {
          points = maxPoints;
        }
      }
      if (question.type === 'single_select') {
        let correct_options = ((question.correct_options || {})[i18n.resolvedLanguage || 'nl'] || (question.correct_options || {})['nl']).split(/\r?\n/);
        
        if (correct_options.indexOf(answer) > -1) {
          points = maxPoints;
        }
      }
      if (question.type === 'multi') {
        let correct_options = ((question.correct_options || {})[i18n.resolvedLanguage || 'nl'] || (question.correct_options || {})['nl']).split(/\r?\n/);
        
        // @ts-ignore
        if (correct_options.length === answer.length && correct_options.filter(fff => answer.indexOf(fff) < 0).length < 1) {
          points = maxPoints;
        }
      }
    }

    return points;
  }

  useEffect(() => {
    if (params.id !== 'new') objectRefetch();
  }, [params.id]);
  useEffect(() => {
    if (!!objectSuccess) {
      setMainObject({
        ...object,
        is_locked: !!object.completed_at && object.extra_data?.questionnaire?.content_type === 'quiz'
      });
      setAgrees(!!object.completed_at);
    }
  }, [object]);

  return (<Page>
    <Paper sx={{
      padding: 2
    }}>
      <Grid container spacing={1}>
        {!savedOpen && <>
          <Grid item xs={12} sx={{position: 'relative'}}>
            <Typography variant="h1">{(mainObject?.questionnaire?.extra_data?.public_name || {})[i18n.resolvedLanguage || 'nl'] || mainObject?.questionnaire?.name}</Typography>
            <Typography variant="h5">{mainObject?.practice?.name}</Typography>
            <Divider />

            <LanguagePicker sx={{position: 'absolute', top: '0', right: '0'}} />
          </Grid>
          {!!((mainObject?.extra_data?.questionnaire?.intro || {})[i18n.resolvedLanguage || 'nl'] || (mainObject?.extra_data?.questionnaire?.intro || {})['nl']) && <Grid item xs={12}>
            <Typography>{(mainObject?.extra_data?.questionnaire?.intro || {})[i18n.resolvedLanguage || 'nl'] || (mainObject?.extra_data?.questionnaire?.intro || {})['nl']}</Typography>
          </Grid>}

          {!!mainObject?.questionnaire && <>
            {mainObject?.questionnaire?.content_type === 'review' && <>
              <Grid item xs={12} sx={{
                marginTop: 3
              }}>
                <Box sx={{
                  marginTop: 2,
                  padding: 2,
                  border: '1px solid transparent',
                  borderColor: theme.palette.primary.main,
                  backgroundImage: `linear-gradient(to right, transparent, ${theme.palette.primary.main})`,
                  borderRadius: 2
                }}>
                  <Typography variant="h2">{t("client.questionnaire_result.review.privacy_title")}</Typography>
                  <Typography variant="caption">{t("client.questionnaire_result.review.privacy_description")}</Typography>
                  <Divider />
                </Box>
              </Grid>
              <Grid item xs={12} sx={{
                marginTop: 3,
                paddingLeft: "32px !important"
              }}>
                <Typography variant="h4">{t("client.questionnaire_result.review.setting.is_anon")}</Typography>
                <Typography variant="caption">{t("client.questionnaire_result.review.setting.is_anon_description")}</Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} sx={{
                paddingLeft: "32px !important"
              }}>
                <FormControl>
                  <RadioGroup
                    name={`q_priv_0`}
                    value={mainObject?.extra_data?.privacy?.is_anon || '1'}
                    onChange={(e) => {
                      setMainObject({
                        ...mainObject,
                        extra_data: {
                          ...(mainObject.extra_data || {}),
                          privacy: {
                            ...(mainObject.extra_data?.privacy || {}),
                            is_anon: e.target.value
                          }
                        }
                      });
                    }}
                  >
                    <FormControlLabel value="0" control={<Radio />} label={t("client.questionnaire_result.review.setting.is_anon_option.be_known")} />
                    <FormControlLabel value="1" control={<Radio />} label={t("client.questionnaire_result.review.setting.is_anon_option.be_anon")} />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </>}


            {(mainObject?.extra_data?.questionnaire?.questions || []).map((question, i) => <>
              {!!question.section && <>
                <Grid item xs={12} sx={{
                  marginTop: 3
                }}>
                  <Box sx={{
                    marginTop: 2,
                    padding: 2,
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    backgroundImage: `linear-gradient(to right, transparent, ${theme.palette.primary.main})`,
                    borderRadius: 2
                  }}>
                    {!!((question.section_title || {})[i18n.resolvedLanguage || 'nl'] || (question.section_title || {})['nl']) && <Typography variant="h2">{(question.section_title || {})[i18n.resolvedLanguage || 'nl'] || (question.section_title || {})['nl']}</Typography>}
                    {!!((question.section_content || {})[i18n.resolvedLanguage || 'nl'] || (question.section_content || {})['nl']) && <Typography variant="caption">{(question.section_content || {})[i18n.resolvedLanguage || 'nl'] || (question.section_content || {})['nl']}</Typography>}
                    <Divider />
                  </Box>
                </Grid>
              </>}
              {!question.section && <>
                <Grid item xs={12} sx={{
                  marginTop: 3,
                  paddingLeft: "32px !important",
                  position: 'relative'
                }}>
                  <Typography variant="h4">{(question.question || {})[i18n.resolvedLanguage || 'nl'] || (question.question || {})['nl']} {!!question.required ? "*" : ""}</Typography>
                  {!!((question.help || {})[i18n.resolvedLanguage || 'nl'] || (question.help || {})['nl']) && <Typography variant="caption">{(question.help || {})[i18n.resolvedLanguage || 'nl'] || (question.help || {})['nl']}</Typography>}
                  {!!mainObject.is_locked && mainObject?.extra_data?.questionnaire?.content_type === 'quiz' && !!question.quiz_points && <Box sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0
                  }}>
                    {calcPoints(question, (mainObject?.extra_data?.answers || [])[i])} / {question.quiz_points}
                  </Box>}
                  <Divider />
                </Grid>
                {!mainObject?.is_locked && <>
                  {question.type === 'existing' && <>
                    {[
                      'profile|first_name', 'profile|last_name', 'profile|email', 'profile|phone_mobile', 'profile|phone_fixed',
                      'intake|practician', 'intake|insurance', 'intake|extra_insurance', 'intake|convention',
                      'intake|weight_progress', 
                      'intake|children', 
                      'intake|hours_sit', 'intake|hours_sleep', 'intake|hours_screen', 
                      'intake|diagnoses', 'intake|complaints', 'intake|medication', 'intake|allergies', 'intake|history',
                      'intake|situation_home', 'intake|situation_work', 'intake|hobbies', 'intake|sports', 'intake|comment_smoker', 'intake|comment_alcohol', 'intake|relaxation', 'intake|food_pattern',
                      'intake|consult_reason', 'intake|consult_expectation', 'intake|goals', 
                    ].indexOf(question.existing_question_id || '') > -1 && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <TextInput
                        id={`q${i}`}
                        noCaption
                        multiline={[
                          'intake|practician', 'intake|insurance', 'intake|extra_insurance', 'intake|convention',
                          'intake|weight_progress', 
                          'intake|children', 
                          'intake|diagnoses', 'intake|complaints', 'intake|medication', 'intake|allergies', 'intake|history',
                          'intake|situation_home', 'intake|situation_work', 'intake|hobbies', 'intake|sports', 'intake|comment_smoker', 'intake|comment_alcohol', 'intake|relaxation', 'intake|food_pattern',
                          'intake|consult_reason', 'intake|consult_expectation', 'intake|goals', 
                        ].indexOf(question.existing_question_id || '') > -1}
                        endIcon={((!!question.min && ((mainObject?.extra_data?.answers || [])[i] || '').length < question.min) || !!question.max && ((mainObject?.extra_data?.answers || [])[i] || '').length > question.max) ? <PriorityHighIcon color="secondary" /> : undefined}
                        value={(mainObject?.extra_data?.answers || [])[i] || ''}
                        onChange={(e) => {
                          let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                          newAnswers[i] = e;

                          setMainObject({
                            ...mainObject,
                            extra_data: {
                              ...(mainObject.extra_data || {}),
                              answers: newAnswers
                            }
                          });
                        }}
                        sx={{
                          marginTop: 0
                        }}
                      />
                    </Grid>}
                    {[
                      'intake|height', 'intake|weight', 
                      'intake|stress_score', 
                      'intake|motivation_score', 
                    ].indexOf(question.existing_question_id || '') > -1 && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <TextInput
                        id={`q${i}`}
                        noCaption
                        endIcon={((!!question.min && ((mainObject?.extra_data?.answers || [])[i] || '').length < question.min) || !!question.max && ((mainObject?.extra_data?.answers || [])[i] || '').length > question.max) ? <PriorityHighIcon color="secondary" /> : undefined}
                        value={(mainObject?.extra_data?.answers || [])[i] || ''}
                        onChange={(e) => {
                          let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                          newAnswers[i] = !!e ? e.replace(/[^0-9\,\.]/g, '').replace(",", ".") : undefined;

                          setMainObject({
                            ...mainObject,
                            extra_data: {
                              ...(mainObject.extra_data || {}),
                              answers: newAnswers
                            }
                          });
                        }}
                        sx={{
                          marginTop: 0
                        }}
                      />
                    </Grid>}
                    {[
                      'intake|civil_state', 
                    ].indexOf(question.existing_question_id || '') > -1 && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <Select
                        fullWidth
                        noCaption
                        displayEmpty
                        id="civil_state"
                        value={(mainObject?.extra_data?.answers || [])[i]}
                        setValue={(v) => {
                          let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                          newAnswers[i] = v.toString() === '' ? undefined : parseInt(v.toString(), 10);

                          setMainObject({
                            ...mainObject,
                            extra_data: {
                              ...(mainObject.extra_data || {}),
                              answers: newAnswers
                            }
                          });
                        }}
                        sx={{
                          marginTop: 0
                        }}
                      >
                        <MenuItem value="">---{t("client.models.intake_profiles.civil_state")}---</MenuItem>
                        <MenuItem value={0}>{t("general.civil_states.single")}</MenuItem>
                        <MenuItem value={1}>{t("general.civil_states.married")}</MenuItem>
                        <MenuItem value={2}>{t("general.civil_states.living_together")}</MenuItem>
                      </Select>
                    </Grid>}
                    {[
                      'intake|consult_initiative', 
                    ].indexOf(question.existing_question_id || '') > -1 && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <Select
                        fullWidth
                        noCaption
                        displayEmpty
                        id="consult_initiative"
                        value={(mainObject?.extra_data?.answers || [])[i]}
                        setValue={(v) => {
                          let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                          newAnswers[i] = v.toString() === '' ? undefined : parseInt(v.toString(), 10);

                          setMainObject({
                            ...mainObject,
                            extra_data: {
                              ...(mainObject.extra_data || {}),
                              answers: newAnswers
                            }
                          });
                        }}
                        sx={{
                          marginTop: 0
                        }}
                      >
                        <MenuItem value="">---{t("client.models.intake_profiles.consult_initiative")}---</MenuItem>
                        <MenuItem value="0">{t("general.consult_initiatives.me")}</MenuItem>
                        <MenuItem value="1">{t("general.consult_initiatives.doctor")}</MenuItem>
                        <MenuItem value="2">{t("general.consult_initiatives.specialist")}</MenuItem>
                        <MenuItem value="3">{t("general.consult_initiatives.medical_asst")}</MenuItem>
                        <MenuItem value="4">{t("general.consult_initiatives.family")}</MenuItem>
                        <MenuItem value="5">{t("general.consult_initiatives.other")}</MenuItem>
                      </Select>
                    </Grid>}
                    {[
                      'intake|is_smoker', 'intake|is_alcohol', 'intake|is_sleep_okay',  'intake|doctor_note', 
                    ].indexOf(question.existing_question_id || '') > -1 && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <FormControl>
                        <RadioGroup
                          name={`q${i}`}
                          value={(mainObject?.extra_data?.answers || [])[i] || ''}
                          onChange={(e) => {
                            let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                            newAnswers[i] = e.target.value;
      
                            setMainObject({
                              ...mainObject,
                              extra_data: {
                                ...(mainObject.extra_data || {}),
                                answers: newAnswers
                              }
                            });
                          }}
                        >
                          <FormControlLabel value={0} control={<Radio />} label={t("coach.submenu.agenda.appointment_detail.option_no")} />
                          <FormControlLabel value={1} control={<Radio />} label={t("coach.submenu.agenda.appointment_detail.option_yes")} />
                        </RadioGroup>
                      </FormControl>
                    </Grid>}
                    {/* {[
                      'profile|first_name', 'profile|last_name', 'profile|email', 'profile|phone_mobile', 'profile|phone_fixed',
                    ].indexOf(question.existing_question_id || '') > -1 && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <Select
                        fullWidth
                        noCaption
                        displayEmpty
                        id="coach"
                        value={(mainObject?.extra_data?.answers || [])[i] || ''}
                        setValue={(v) => {
                          let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                          newAnswers[i] = v;

                          setMainObject({
                            ...mainObject,
                            extra_data: {
                              ...(mainObject.extra_data || {}),
                              answers: newAnswers
                            }
                          });
                        }}
                      >
                        <MenuItem value="">--- ---</MenuItem>
                        {((question.options || {})[i18n.resolvedLanguage || 'nl'] || (question.options || {})['nl']).split(/\r?\n/).map(option =>  <MenuItem value={option}>{option}</MenuItem>)}
                      </Select>
                    </Grid>} */}
                  </>}
                  {question.type !== 'existing' && <>
                    {question.type === 'open' && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <TextInput
                        id={`q${i}`}
                        noCaption
                        endIcon={((!!question.min && ((mainObject?.extra_data?.answers || [])[i] || '').length < question.min) || !!question.max && ((mainObject?.extra_data?.answers || [])[i] || '').length > question.max) ? <PriorityHighIcon color="secondary" /> : undefined}
                        value={(mainObject?.extra_data?.answers || [])[i] || ''}
                        onChange={(e) => {
                          let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                          newAnswers[i] = e;

                          setMainObject({
                            ...mainObject,
                            extra_data: {
                              ...(mainObject.extra_data || {}),
                              answers: newAnswers
                            }
                          });
                        }}
                        sx={{
                          marginTop: 0
                        }}
                      />
                      {!!question.max && <Typography>{question.max - ((mainObject?.extra_data?.answers || [])[i] || '').length} {t("general.terms.characters_left")}</Typography>}
                    </Grid>}
                    {question.type === 'open_long' && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <TextInput
                        id={`q${i}`}
                        noCaption
                        endIcon={((!!question.min && ((mainObject?.extra_data?.answers || [])[i] || '').length < question.min) || !!question.max && ((mainObject?.extra_data?.answers || [])[i] || '').length > question.max) ? <PriorityHighIcon color="secondary" /> : undefined}
                        multiline
                        value={(mainObject?.extra_data?.answers || [])[i] || ''}
                        onChange={(e) => {
                          let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                          newAnswers[i] = e;

                          setMainObject({
                            ...mainObject,
                            extra_data: {
                              ...(mainObject.extra_data || {}),
                              answers: newAnswers
                            }
                          });
                        }}
                        sx={{
                          marginTop: 0
                        }}
                      />
                      {!!question.max && <Typography>{question.max - ((mainObject?.extra_data?.answers || [])[i] || '').length} {t("general.terms.characters_left")}</Typography>}
                    </Grid>}
                    {question.type === 'single_radio' && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <FormControl>
                        <RadioGroup
                          name={`q${i}`}
                          value={(mainObject?.extra_data?.answers || [])[i] || ''}
                          onChange={(e) => {
                            let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                            newAnswers[i] = e.target.value;
      
                            setMainObject({
                              ...mainObject,
                              extra_data: {
                                ...(mainObject.extra_data || {}),
                                answers: newAnswers
                              }
                            });
                          }}
                        >
                          {((question.options || {})[i18n.resolvedLanguage || 'nl'] || (question.options || {})['nl']).split(/\r?\n/).map(option => <FormControlLabel value={option} control={<Radio />} label={option} />)}
                        </RadioGroup>
                      </FormControl>
                    </Grid>}
                    {question.type === 'single_select' && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <Select
                        fullWidth
                        noCaption
                        displayEmpty
                        id="coach"
                        value={(mainObject?.extra_data?.answers || [])[i] || ''}
                        setValue={(v) => {
                          let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                          newAnswers[i] = v;

                          setMainObject({
                            ...mainObject,
                            extra_data: {
                              ...(mainObject.extra_data || {}),
                              answers: newAnswers
                            }
                          });
                        }}
                      >
                        <MenuItem value="">--- ---</MenuItem>
                        {(((question.options || {})[i18n.resolvedLanguage || 'nl'] || (question.options || {})['nl']) || '').split(/\r?\n/).map(option =>  <MenuItem value={option}>{option}</MenuItem>)}
                      </Select>
                    </Grid>}
                    {question.type === 'multi' && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <FormGroup sx={{display: 'flex'}}>
                        {(((question.options || {})[i18n.resolvedLanguage || 'nl'] || (question.options || {})['nl']) || '').split(/\r?\n/).map(option => <FormControlLabel control={<Checkbox
                          checked={typeof(((mainObject?.extra_data?.answers || [])[i] || [])) === 'number' ? ((mainObject?.extra_data?.answers || [])[i] || []).toString() : ((mainObject?.extra_data?.answers || [])[i] || []).indexOf(option) > -1}
                          value={option}
                          onChange={(e) => {
                            let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                            if (typeof(newAnswers[i]) === 'undefined') newAnswers[i] = [];
                            if (e.target.checked) {
                              newAnswers[i] = [
                                ...(newAnswers[i] || []),
                                option
                              ];
                            } else {
                              // @ts-ignore
                              newAnswers[i] = (typeof(newAnswers[i]) === 'number' ? [newAnswers[i]] : newAnswers[i]).filter(fff => fff !== option);
                            }

                            setMainObject({
                              ...mainObject,
                              extra_data: {
                                ...(mainObject.extra_data || {}),
                                answers: newAnswers
                              }
                            });
                          }}
                        />} label={option} sx={{display: 'inline'}} />)}
                      </FormGroup>
                    </Grid>}
                    {question.type === 'nps' && <Grid item xs={12} sx={{
                      paddingLeft: "32px !important"
                    }}>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-evenly'
                      }}>
                        {Array((question?.max || 10) + 1 - (question?.min || 1)).fill(0).map((_, idx) => (question?.min || 1) + idx).map(v => <>
                          <Box sx={{
                            border: '1px solid transparent',
                            borderColor: theme.palette.primary.main,
                            padding: 2,
                            fontSize: '1.1rem',
                            display: 'inline-block',
                            cursor: 'pointer',
                            margin: '-0.5px',
                            // borderRadius: 4,
                            width: `${100 / ((question?.max || 10) + 1 - (question?.min || 1))}%`,
                            textAlign: 'center',
                            "&:hover": {
                              // backgroundColor: 'lightgrey',
                              backgroundColor: theme.palette.primary.light,
                              borderColor: theme.palette.primary.dark
                            },
                            ...((mainObject?.extra_data?.answers || [])[i] === v ? {
                              // backgroundColor: 'lightgrey',
                              backgroundColor: theme.palette.primary.light,
                              borderColor: theme.palette.primary.dark
                            } : {})
                          }} onClick={() => {
                            let newAnswers:any[] = [...(mainObject.extra_data?.answers || [])];
                            newAnswers[i] = v;

                            setMainObject({
                              ...mainObject,
                              extra_data: {
                                ...(mainObject.extra_data || {}),
                                answers: newAnswers
                              }
                            });
                          }}>{v}</Box>
                        </>)}
                      </Box>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-evenly'
                      }}>
                        <Box sx={{
                          display: 'inline-block',
                          margin: '-0.5px',
                          width: '50%',
                          textAlign: 'left',
                          marginTop: 2,
                          fontStyle: 'italic'
                        }}><Typography sx={{
                          fontSize: '0.9rem',
                          color: 'grey',
                        }}>{t("question.nps.unlikely_term")}</Typography></Box>
                        <Box sx={{
                          fontSize: '1.1rem',
                          display: 'inline-block',
                          margin: '-0.5px',
                          width: '50%',
                          textAlign: 'right',
                          color: 'grey',
                          marginTop: 2,
                          fontStyle: 'italic'
                        }}><Typography sx={{
                          fontSize: '0.9rem',
                          color: 'grey',
                        }}>{t("question.nps.likely_term")}</Typography></Box>
                      </Box>
                    </Grid>}
                  </>}
                </>}
                {!!mainObject?.is_locked && <>
                  {question.type === 'existing' && <Grid item xs={12} sx={{
                    paddingLeft: "32px !important"
                  }}>
                    <Typography sx={{marginTop: 1, paddingLeft: "32px !important", borderLeft: '2px solid transparent', borderColor: theme.palette.primary.main}}>{(mainObject?.extra_data?.answers || [])[i] || ''}</Typography>
                  </Grid>}
                  {question.type === 'open' && <Grid item xs={12} sx={{
                    paddingLeft: "32px !important"
                  }}>
                    <Typography sx={{marginTop: 1, paddingLeft: "32px !important", borderLeft: '2px solid transparent', borderColor: theme.palette.primary.main}}>{(mainObject?.extra_data?.answers || [])[i] || ''}</Typography>
                    {mainObject?.extra_data?.questionnaire?.content_type === 'quiz' && <>
                      {((mainObject?.extra_data?.answers || [])[i] || '').toLowerCase() === (((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_open || '').toLowerCase() && <>
                        <Typography>{t("questionnaires.quiz.correct_answer")}</Typography>
                      </>}
                      {((mainObject?.extra_data?.answers || [])[i] || '').toLowerCase() !== (((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_open || '').toLowerCase() && <>
                        <Typography sx={{color: theme.palette.secondary.main}}>{t("questionnaires.quiz.wrong_answer")}</Typography>
                        <Typography sx={{marginTop: 1, paddingLeft: "32px !important", borderLeft: '2px solid transparent', borderColor: theme.palette.secondary.main}}>{((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_open || ''}</Typography>
                      </>}
                    </>}
                  </Grid>}
                  {question.type === 'open_long' && <Grid item xs={12} sx={{
                    paddingLeft: "32px !important"
                  }}>
                    <Typography sx={{marginTop: 1, paddingLeft: "32px !important", borderLeft: '2px solid transparent', borderColor: theme.palette.primary.main}}>{(mainObject?.extra_data?.answers || [])[i] || ''}</Typography>
                  </Grid>}
                  {question.type === 'single_radio' && <Grid item xs={12} sx={{
                    paddingLeft: "32px !important"
                  }}>
                    {mainObject?.extra_data?.questionnaire?.content_type !== 'quiz' && <>
                      {((question.options || {})[i18n.resolvedLanguage || 'nl'] || (question.options || {})['nl']).split(/\r?\n/).map(option => <Typography sx={{opacity: ((mainObject?.extra_data?.answers || [])[i] === option ? '1' : '0.3')}}>{option}</Typography>)}
                    </>}
                    {mainObject?.extra_data?.questionnaire?.content_type === 'quiz' && <>
                      {((question.options || {})[i18n.resolvedLanguage || 'nl'] || (question.options || {})['nl']).split(/\r?\n/).map(option => <Typography sx={{
                        opacity: ((mainObject?.extra_data?.answers || [])[i] === option ? '1' : '0.3'),
                        color: option === ((((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_options || {})[i18n.resolvedLanguage || 'nl'] || (((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_options || {})['nl']).split(/\r?\n/)[0] ? theme.palette.primary.main : theme.palette.secondary.main
                      }}>{option}</Typography>)}
                    </>}
                  </Grid>}
                  {question.type === 'single_select' && <Grid item xs={12} sx={{
                    paddingLeft: "32px !important"
                  }}>
                    <Typography sx={{marginTop: 1, paddingLeft: "32px !important", borderLeft: '2px solid transparent', borderColor: theme.palette.primary.main}}>{(mainObject?.extra_data?.answers || [])[i] || ''}</Typography>
                    {mainObject?.extra_data?.questionnaire?.content_type === 'quiz' && <>
                      {((mainObject?.extra_data?.answers || [])[i] || '') === ((((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_options || {})[i18n.resolvedLanguage || 'nl'] || (((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_options || {})['nl']).split(/\r?\n/)[0] && <>
                        <Typography>{t("questionnaires.quiz.correct_answer")}</Typography>
                      </>}
                      {((mainObject?.extra_data?.answers || [])[i] || '') !== ((((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_options || {})[i18n.resolvedLanguage || 'nl'] || (((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_options || {})['nl']).split(/\r?\n/)[0] && <>
                        <Typography sx={{color: theme.palette.secondary.main}}>{t("questionnaires.quiz.wrong_answer")}</Typography>
                        <Typography sx={{marginTop: 1, paddingLeft: "32px !important", borderLeft: '2px solid transparent', borderColor: theme.palette.secondary.main}}>{((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_open || ''}</Typography>
                      </>}
                    </>}
                  </Grid>}
                  {question.type === 'multi' && <Grid item xs={12} sx={{
                    paddingLeft: "32px !important"
                  }}>
                    {mainObject?.extra_data?.questionnaire?.content_type !== 'quiz' && <>
                      {((question.options || {})[i18n.resolvedLanguage || 'nl'] || (question.options || {})['nl']).split(/\r?\n/).map(option => <Typography sx={{opacity: (((mainObject?.extra_data?.answers || [])[i] || []).indexOf(option) > -1 ? '1' : '0.3')}}>{option}</Typography>)}
                    </>}
                    {mainObject?.extra_data?.questionnaire?.content_type === 'quiz' && <>
                      {((question.options || {})[i18n.resolvedLanguage || 'nl'] || (question.options || {})['nl']).split(/\r?\n/).map(option => <Typography sx={{
                        opacity: (((mainObject?.extra_data?.answers || [])[i] || []).indexOf(option) > -1 ? '1' : '0.3'),
                        color: ((((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_options || {})[i18n.resolvedLanguage || 'nl'] || (((mainObject?.extra_data?.questionnaire?.questions || [])[i] || {}).correct_options || {})['nl']).split(/\r?\n/).indexOf(option) > -1 ? theme.palette.primary.main : theme.palette.secondary.main
                      }}>{option}</Typography>)}
                    </>}
                  </Grid>}
                  {question.type === 'nps' && <Grid item xs={12} sx={{
                    paddingLeft: "32px !important"
                  }}>
                    <Typography sx={{marginTop: 1, paddingLeft: "32px !important", borderLeft: '2px solid transparent', borderColor: theme.palette.primary.main}}>{(mainObject?.extra_data?.answers || [])[i] || ''}/{question.max || 10}</Typography>  
                  </Grid>}
                </>}
              </>}
            </>)}
          </>}



          <Grid item xs={12} sx={{
            paddingLeft: "32px !important",
            marginTop: 4
          }}>
            <FormControlLabel control={<Checkbox
              checked={!!agrees}
              disabled={!!mainObject?.is_locked}
              value={agrees}
              onChange={(e) => {
                if (e.target.checked) {
                  setAgrees(true);
                } else {
                  setAgrees(false);
                }
              }}
            />} label={t("client.questionnaire.agree_privacy")} sx={{display: 'inline'}} />
          </Grid>






          <Grid item xs={12} sx={{
            alignContent: 'baseline',
            textAlign: 'center',
            position: {
              xs: 'relative',
              md: 'sticky'
            },
            bottom: '-24px',
            backgroundColor: 'white',
            zIndex: 1201
          }}>
            <Divider sx={{marginTop: 1, marginBottom: 0}} />

            {(mainObject?.extra_data?.questionnaire?.questions || []).filter((fff, i) => fff.required && !(mainObject?.extra_data?.answers || [])[i]).length > 0 && <Typography sx={{fontStyle: 'italic', marginTop: 2, marginBottom: 2, color: theme.palette.secondary.main}}>{t("client.questionnaires.description_button_disabled_req_questions")}</Typography>}

            {!!mutationSave.isPending && <CircularProgress />}
            {!mutationSave.isPending && !mainObject?.is_locked && <Button
              label={t("shared.buttons.save_without_submit")}
              id='save'
              // disabled={(mainObject?.extra_data?.questionnaire?.questions || []).filter((fff, i) => fff.required && !(mainObject?.extra_data?.answers || [])[i]).length > 0}
              contained
              onClick={(v) => {
                mutationSave.mutate({
                  formData: {
                    object: 'questionnaire_result',
                    id: objectId,
                    fields: ['id'],
                    ob: {
                      id: objectId,
                      extra_data: {
                        ...mainObject?.extra_data,
                        answers: [
                          ...[...(mainObject?.extra_data?.answers || [])].map(fff => typeof(fff) === 'undefined' ? '' : fff)
                        ]
                      },
                      // completed_at: DateTime.now().toFormat("y-LL-dd t")
                    }
                  }
                });
              }}
              sx={{marginBottom: 1, marginTop: 1}}
            />}
            {!mutationSave.isPending && !mainObject?.is_locked && <Button
              label={t("shared.buttons.submit")}
              id='submit'
              disabled={!agrees || (mainObject?.extra_data?.questionnaire?.questions || []).filter((fff, i) => fff.required && !(mainObject?.extra_data?.answers || [])[i]).length > 0}
              contained
              onClick={(v) => {
                mutationSave.mutate({
                  formData: {
                    object: 'questionnaire_result',
                    id: objectId,
                    fields: ['id'],
                    ob: {
                      id: objectId,
                      extra_data: {
                        ...mainObject?.extra_data,
                        answers: [
                          ...[...(mainObject?.extra_data?.answers || [])].map(fff => typeof(fff) === 'undefined' ? '' : (fff || ''))
                        ]
                      },
                      completed_at: DateTime.now().toFormat("y-LL-dd t")
                    }
                  }
                });
              }}
              sx={{marginLeft: 1, marginBottom: 1, marginTop: 1}}
            />}
            {!mutationSave.isPending && <Button
              label={t("shared.buttons.cancel")}
              id='cancel'
              text
              // contained
              onClick={(v) => {
                navigate(!!login ? "/questionnaires" : "/");
              }}
              sx={{marginLeft: 1, marginBottom: 1, marginTop: 1}}
            />}
          </Grid>
        </>}

        {!!savedOpen && <>
          <Grid item xs={12} sx={{textAlign: 'center'}}>
            <Typography variant="h3">{t("questionnaire.message.success")}</Typography>
            <Button
              label={t("goto_root")}
              id='goto'
              contained
              onClick={(v) => {
                navigate(!!login ? "/questionnaires" : "/");
              }}
              sx={{marginTop: 1}}
            />
          </Grid>
          {mainObject?.extra_data?.questionnaire?.content_type === 'quiz' && <>
            <Grid item xs={12} sx={{textAlign: 'center'}}>
              quiz!
            </Grid>
          </>}
        </>}
      </Grid>
    </Paper>
    
    <SnackSaved open={savedOpen} setOpen={() => {}} />
  </Page>);
}
